import { FC, useEffect, useRef, useState } from 'react';
import { Editor, OnMount } from '@monaco-editor/react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import * as Diff from 'diff';
import { AppCard, AppPage } from '../../components/general';
import { FeesEmulatorTransactionLookup } from './FeesEmulatorTransactionLookup';
import './FeesEmulator.css';

type EditorInstance = Parameters<OnMount>[0];
type DecorationCollection = ReturnType<EditorInstance['createDecorationsCollection']>;

export interface VersionOption {
  label: string;
  value: number | null;
}

interface FeesEmulatorProps {
  payload: string;
  onPayloadChange: (next: string) => void;
  parseError: string | null;
  versionOptions: VersionOption[];
  versionAId: number | null;
  versionBId: number | null;
  onVersionAChange: (id: number | null) => void;
  onVersionBChange: (id: number | null) => void;
  responseAJson: string;
  responseBJson: string;
  errorA: string | null;
  errorB: string | null;
  isRunning: boolean;
  canRun: boolean;
  onRun: () => void;
  onLookupLoad: (json: string) => void;
  onLookupError: (msg: string) => void;
}

/**
 * One logical diff: a region where the two responses differ. `origLines` and `modLines` are the
 * line spans on each side. Either may be 0 (pure addition or pure removal); when both are non-zero
 * it's a modification (adjacent removed+added in `Diff.diffLines` output).
 */
interface DiffEntry {
  origLine: number;
  origLines: number;
  modLine: number;
  modLines: number;
}

const buildSpacerNode = (height: number): HTMLElement => {
  const node = document.createElement('div');
  node.style.height = `${height * 1.5}em`;
  node.style.background = 'rgba(127, 127, 127, 0.05)';
  return node;
};

/**
 * Three-pane Fees Emulator. Two standalone Monaco editors on the right (in place of the built-in
 * DiffEditor — its embedded inner editors don't render the folding gutter, which we need for
 * click-to-fold/expand). We recompute alignment and decorations on every response change:
 *   1. `Diff.diffLines` produces chunks; we walk them into a `DiffEntry[]`.
 *   2. Line decorations highlight added (green) / removed (red) ranges per side.
 *   3. View zones inserted on the shorter side keep the two panes vertically aligned, the same
 *      mechanism the built-in DiffEditor uses internally.
 *   4. Navigation buttons (First / Prev / Next / Last) scroll both editors to the focused diff and
 *      paint an amber overlay on it.
 */
export const FeesEmulator: FC<FeesEmulatorProps> = ({
  payload,
  onPayloadChange,
  parseError,
  versionOptions,
  versionAId,
  versionBId,
  onVersionAChange,
  onVersionBChange,
  responseAJson,
  responseBJson,
  errorA,
  errorB,
  isRunning,
  canRun,
  onRun,
  onLookupLoad,
  onLookupError,
}) => {
  const origEditorRef = useRef<EditorInstance | null>(null);
  const modEditorRef = useRef<EditorInstance | null>(null);
  const origDecorationsRef = useRef<DecorationCollection | null>(null);
  const modDecorationsRef = useRef<DecorationCollection | null>(null);
  const origFocusDecorationsRef = useRef<DecorationCollection | null>(null);
  const modFocusDecorationsRef = useRef<DecorationCollection | null>(null);
  const origZoneIdsRef = useRef<string[]>([]);
  const modZoneIdsRef = useRef<string[]>([]);
  const diffsRef = useRef<DiffEntry[]>([]);
  // Re-entrancy guard for scroll sync. Without this, A→B sync triggers B's onDidScrollChange,
  // which tries to sync back to A, creating a feedback loop.
  const isSyncingScrollRef = useRef(false);
  const [diffCount, setDiffCount] = useState(0);
  const [currentDiffIdx, setCurrentDiffIdx] = useState(-1);
  const [showOnlyDiffs, setShowOnlyDiffs] = useState(true);
  // Lines of context to keep visible above/below each diff when "Only diffs" is active.
  const CONTEXT_LINES = 3;

  const handleOrigMount: OnMount = editor => {
    origEditorRef.current = editor;
    origDecorationsRef.current = editor.createDecorationsCollection([]);
    origFocusDecorationsRef.current = editor.createDecorationsCollection([]);
    editor.onDidScrollChange(e => {
      if (isSyncingScrollRef.current) return;
      const target = modEditorRef.current;
      if (!target) return;
      isSyncingScrollRef.current = true;
      target.setScrollPosition({ scrollTop: e.scrollTop, scrollLeft: e.scrollLeft });
      isSyncingScrollRef.current = false;
    });
  };

  const handleModMount: OnMount = editor => {
    modEditorRef.current = editor;
    modDecorationsRef.current = editor.createDecorationsCollection([]);
    modFocusDecorationsRef.current = editor.createDecorationsCollection([]);
    editor.onDidScrollChange(e => {
      if (isSyncingScrollRef.current) return;
      const target = origEditorRef.current;
      if (!target) return;
      isSyncingScrollRef.current = true;
      target.setScrollPosition({ scrollTop: e.scrollTop, scrollLeft: e.scrollLeft });
      isSyncingScrollRef.current = false;
    });
  };

  // Compute the diff, apply line decorations, insert alignment view zones. Runs whenever either
  // response (or the editor instances) change.
  useEffect(() => {
    const origEd = origEditorRef.current;
    const modEd = modEditorRef.current;
    const origCollection = origDecorationsRef.current;
    const modCollection = modDecorationsRef.current;
    if (!origEd || !modEd || !origCollection || !modCollection) return;

    const chunks = Diff.diffLines(responseAJson, responseBJson);
    const origDecorations: Array<{ range: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number }; options: { isWholeLine: boolean; className: string } }> = [];
    const modDecorations: typeof origDecorations = [];
    const diffs: DiffEntry[] = [];
    let origLine = 1;
    let modLine = 1;

    // Pre-resolve the chunks array; we walk with an index so we can peek ahead to merge adjacent
    // removed+added pairs into a single DiffEntry (a modification).
    const items = chunks.map(c => ({ ...c, lines: c.count ?? 0 }));
    let i = 0;
    while (i < items.length) {
      const cur = items[i];
      const next = items[i + 1];
      if (cur.removed && next?.added) {
        // Modification: emit one DiffEntry covering both sides.
        diffs.push({
          origLine,
          origLines: cur.lines,
          modLine,
          modLines: next.lines,
        });
        if (cur.lines > 0) {
          origDecorations.push({
            range: { startLineNumber: origLine, startColumn: 1, endLineNumber: origLine + cur.lines - 1, endColumn: 1 },
            options: { isWholeLine: true, className: 'feeEmu-line-removed' },
          });
        }
        if (next.lines > 0) {
          modDecorations.push({
            range: { startLineNumber: modLine, startColumn: 1, endLineNumber: modLine + next.lines - 1, endColumn: 1 },
            options: { isWholeLine: true, className: 'feeEmu-line-added' },
          });
        }
        origLine += cur.lines;
        modLine += next.lines;
        i += 2;
        continue;
      }
      if (cur.added) {
        diffs.push({ origLine, origLines: 0, modLine, modLines: cur.lines });
        if (cur.lines > 0) {
          modDecorations.push({
            range: { startLineNumber: modLine, startColumn: 1, endLineNumber: modLine + cur.lines - 1, endColumn: 1 },
            options: { isWholeLine: true, className: 'feeEmu-line-added' },
          });
        }
        modLine += cur.lines;
        i += 1;
        continue;
      }
      if (cur.removed) {
        diffs.push({ origLine, origLines: cur.lines, modLine, modLines: 0 });
        if (cur.lines > 0) {
          origDecorations.push({
            range: { startLineNumber: origLine, startColumn: 1, endLineNumber: origLine + cur.lines - 1, endColumn: 1 },
            options: { isWholeLine: true, className: 'feeEmu-line-removed' },
          });
        }
        origLine += cur.lines;
        i += 1;
        continue;
      }
      // Unchanged
      origLine += cur.lines;
      modLine += cur.lines;
      i += 1;
    }

    origCollection.set(origDecorations);
    modCollection.set(modDecorations);

    // Replace alignment view zones with newly-computed ones. View zones add visual height without
    // touching the underlying model; we insert them on the *shorter* side of each diff so the two
    // panes stay vertically aligned the way the built-in DiffEditor does.
    origEd.changeViewZones(accessor => {
      origZoneIdsRef.current.forEach(id => accessor.removeZone(id));
      origZoneIdsRef.current = [];
      for (const d of diffs) {
        const gap = d.modLines - d.origLines;
        if (gap > 0) {
          const id = accessor.addZone({
            // afterLineNumber 0 is valid and means "before line 1"; for non-zero anchor on the last
            // line of this region (or the prior unchanged line for pure additions).
            afterLineNumber: d.origLines > 0 ? d.origLine + d.origLines - 1 : d.origLine - 1,
            heightInLines: gap,
            domNode: buildSpacerNode(gap),
          });
          origZoneIdsRef.current.push(id);
        }
      }
    });

    modEd.changeViewZones(accessor => {
      modZoneIdsRef.current.forEach(id => accessor.removeZone(id));
      modZoneIdsRef.current = [];
      for (const d of diffs) {
        const gap = d.origLines - d.modLines;
        if (gap > 0) {
          const id = accessor.addZone({
            afterLineNumber: d.modLines > 0 ? d.modLine + d.modLines - 1 : d.modLine - 1,
            heightInLines: gap,
            domNode: buildSpacerNode(gap),
          });
          modZoneIdsRef.current.push(id);
        }
      }
    });

    diffsRef.current = diffs;
    setDiffCount(diffs.length);
    setCurrentDiffIdx(-1);
    origFocusDecorationsRef.current?.set([]);
    modFocusDecorationsRef.current?.set([]);

    // Diffs-only mode: hide unchanged regions, keeping CONTEXT_LINES on each side of every diff.
    // For pure additions/removals we still keep context on the silent side around the spot where
    // the change would have appeared, so the gap on that side is anchored to surrounding keys.
    const computeHiddenInverse = (lineCount: number, kept: Array<[number, number]>) => {
      if (lineCount <= 0) return [];
      if (kept.length === 0) return [{ startLineNumber: 1, endLineNumber: lineCount }];
      const sorted = [...kept].sort((a, b) => a[0] - b[0]);
      const merged: Array<[number, number]> = [];
      for (const [s, e] of sorted) {
        if (merged.length && s <= merged[merged.length - 1][1] + 1) {
          merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
        } else {
          merged.push([s, e]);
        }
      }
      const hidden: Array<{ startLineNumber: number; endLineNumber: number }> = [];
      let prev = 0;
      for (const [s, e] of merged) {
        if (s - 1 > prev) hidden.push({ startLineNumber: prev + 1, endLineNumber: s - 1 });
        prev = e;
      }
      if (prev < lineCount) hidden.push({ startLineNumber: prev + 1, endLineNumber: lineCount });
      return hidden;
    };

    if (showOnlyDiffs && diffs.length > 0) {
      const origLineCount = origEd.getModel()?.getLineCount() ?? 0;
      const modLineCount = modEd.getModel()?.getLineCount() ?? 0;
      const origKept: Array<[number, number]> = [];
      const modKept: Array<[number, number]> = [];
      for (const d of diffs) {
        const origAnchor = d.origLines > 0 ? d.origLine + d.origLines - 1 : d.origLine - 1;
        const modAnchor = d.modLines > 0 ? d.modLine + d.modLines - 1 : d.modLine - 1;
        origKept.push([
          Math.max(1, d.origLine - CONTEXT_LINES),
          Math.min(origLineCount, Math.max(d.origLine, origAnchor) + CONTEXT_LINES),
        ]);
        modKept.push([
          Math.max(1, d.modLine - CONTEXT_LINES),
          Math.min(modLineCount, Math.max(d.modLine, modAnchor) + CONTEXT_LINES),
        ]);
      }
      (origEd as any).setHiddenAreas?.(computeHiddenInverse(origLineCount, origKept));
      (modEd as any).setHiddenAreas?.(computeHiddenInverse(modLineCount, modKept));
    } else {
      (origEd as any).setHiddenAreas?.([]);
      (modEd as any).setHiddenAreas?.([]);
    }
  }, [responseAJson, responseBJson, showOnlyDiffs]);

  const focusDiff = (idx: number) => {
    const diffs = diffsRef.current;
    if (idx < 0 || idx >= diffs.length) return;
    const d = diffs[idx];
    setCurrentDiffIdx(idx);

    const origLineToReveal = d.origLines > 0 ? d.origLine : Math.max(1, d.origLine);
    const modLineToReveal = d.modLines > 0 ? d.modLine : Math.max(1, d.modLine);
    origEditorRef.current?.revealLineInCenter(origLineToReveal);
    modEditorRef.current?.revealLineInCenter(modLineToReveal);

    if (d.origLines > 0) {
      origFocusDecorationsRef.current?.set([
        {
          range: { startLineNumber: d.origLine, startColumn: 1, endLineNumber: d.origLine + d.origLines - 1, endColumn: 1 },
          options: { isWholeLine: true, className: 'feeEmu-line-focused' },
        },
      ]);
    } else {
      origFocusDecorationsRef.current?.set([]);
    }
    if (d.modLines > 0) {
      modFocusDecorationsRef.current?.set([
        {
          range: { startLineNumber: d.modLine, startColumn: 1, endLineNumber: d.modLine + d.modLines - 1, endColumn: 1 },
          options: { isWholeLine: true, className: 'feeEmu-line-focused' },
        },
      ]);
    } else {
      modFocusDecorationsRef.current?.set([]);
    }
  };

  const handleFirstDiff = () => focusDiff(0);
  const handlePrevDiff = () => focusDiff(currentDiffIdx <= 0 ? 0 : currentDiffIdx - 1);
  const handleNextDiff = () => focusDiff(currentDiffIdx < 0 ? 0 : Math.min(currentDiffIdx + 1, diffCount - 1));
  const handleLastDiff = () => focusDiff(diffCount - 1);

  const handleCollapseAll = () => {
    origEditorRef.current?.getAction('editor.foldAll')?.run();
    modEditorRef.current?.getAction('editor.foldAll')?.run();
  };

  const handleExpandAll = () => {
    origEditorRef.current?.getAction('editor.unfoldAll')?.run();
    modEditorRef.current?.getAction('editor.unfoldAll')?.run();
  };

  const editorOptions = {
    minimap: { enabled: false },
    fontSize: 12,
    automaticLayout: true,
    readOnly: true,
    folding: true,
    foldingHighlight: true,
  } as const;

  const navDisabled = diffCount === 0;
  const diffPositionLabel = diffCount === 0
    ? 'No diffs'
    : `${currentDiffIdx < 0 ? '–' : currentDiffIdx + 1} / ${diffCount}`;

  return (
    <AppPage title="Fees Emulator">
      <div style={{ display: 'flex', gap: '1rem', height: 'calc(100vh - 220px)' }}>
        <div style={{ flex: '0 0 32%', display: 'flex', flexDirection: 'column' }}>
          <AppCard>
            <FeesEmulatorTransactionLookup onLoad={onLookupLoad} onError={onLookupError} />
          </AppCard>
          <div style={{ flex: 1, minHeight: 0, border: '1px solid var(--surface-border)', borderRadius: 4, overflow: 'hidden', marginTop: '0.5rem' }}>
            <Editor
              language="json"
              value={payload}
              onChange={v => onPayloadChange(v ?? '')}
              options={{ minimap: { enabled: false }, fontSize: 12, automaticLayout: true }}
            />
          </div>
          {parseError && <Message severity="error" text={parseError} style={{ marginTop: '0.5rem' }} />}
          <Button
            label={isRunning ? 'Running…' : 'Run'}
            icon={isRunning ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
            onClick={onRun}
            disabled={!canRun || isRunning}
            style={{ marginTop: '0.5rem' }}
          />
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Output A</label>
              <Dropdown
                value={versionAId}
                options={versionOptions}
                optionLabel="label"
                optionValue="value"
                onChange={e => onVersionAChange(e.value)}
                placeholder="Select publication"
                style={{ width: '100%' }}
              />
              {errorA && <Message severity="error" text={errorA} style={{ marginTop: '0.25rem', display: 'block' }} />}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Output B</label>
              <Dropdown
                value={versionBId}
                options={versionOptions}
                optionLabel="label"
                optionValue="value"
                onChange={e => onVersionBChange(e.value)}
                placeholder="Select publication"
                style={{ width: '100%' }}
              />
              {errorB && <Message severity="error" text={errorB} style={{ marginTop: '0.25rem', display: 'block' }} />}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button label="Collapse all" icon="pi pi-minus" onClick={handleCollapseAll} outlined size="small" />
            <Button label="Expand all" icon="pi pi-plus" onClick={handleExpandAll} outlined size="small" />
            <Button
              label={showOnlyDiffs ? 'Show all' : 'Only diffs'}
              icon={showOnlyDiffs ? 'pi pi-eye' : 'pi pi-filter'}
              onClick={() => setShowOnlyDiffs(v => !v)}
              outlined={!showOnlyDiffs}
              size="small"
              disabled={diffCount === 0}
              tooltip={`Toggle: hide unchanged regions, keep ${CONTEXT_LINES} lines context around each diff`}
              tooltipOptions={{ position: 'bottom' }}
            />
            <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', marginLeft: 'auto' }}>
              <Button icon="pi pi-angle-double-left" onClick={handleFirstDiff} disabled={navDisabled} outlined size="small" tooltip="First diff" tooltipOptions={{ position: 'bottom' }} />
              <Button icon="pi pi-angle-left" onClick={handlePrevDiff} disabled={navDisabled} outlined size="small" tooltip="Previous diff" tooltipOptions={{ position: 'bottom' }} />
              <span style={{ fontSize: 12, minWidth: 70, textAlign: 'center' }}>{diffPositionLabel}</span>
              <Button icon="pi pi-angle-right" onClick={handleNextDiff} disabled={navDisabled} outlined size="small" tooltip="Next diff" tooltipOptions={{ position: 'bottom' }} />
              <Button icon="pi pi-angle-double-right" onClick={handleLastDiff} disabled={navDisabled} outlined size="small" tooltip="Last diff" tooltipOptions={{ position: 'bottom' }} />
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex', gap: '0.5rem', minHeight: 0 }}>
            <div style={{ flex: 1, border: '1px solid var(--surface-border)', borderRadius: 4, overflow: 'hidden' }}>
              <Editor
                language="json"
                value={responseAJson}
                onMount={handleOrigMount}
                options={editorOptions}
              />
            </div>
            <div style={{ flex: 1, border: '1px solid var(--surface-border)', borderRadius: 4, overflow: 'hidden' }}>
              <Editor
                language="json"
                value={responseBJson}
                onMount={handleModMount}
                options={editorOptions}
              />
            </div>
          </div>
        </div>
      </div>
    </AppPage>
  );
};

export default FeesEmulator;
